/**
 * A registrar's own export, read column by column onto one of IMAM's source
 * shapes (ADR 0039 Intake, ADR 0036).
 *
 * The registrar brings the spreadsheet they already have: one row per
 * enrollment, with whatever headers their system writes. This module maps
 * those headers onto IMAM's fields and collapses the repeated parent values
 * (a term, a section, a learner) into one typed row each. It guesses nothing:
 *
 * - A field is prefilled with a column only when the column's normalised
 *   header equals the field's id, its label or one of its short documented
 *   aliases, and exactly one column does. Normalised means trimmed, lower
 *   case, and every run of characters other than letters and digits read as
 *   one space, so "Student_ID", "student id" and "STUDENT-ID" are equal.
 *   Nothing else is compared: no spelling distance, no sampling of values.
 * - Every mapping is shown and can be changed to another column or to "Not in
 *   this file". A required field without a column holds the import.
 * - Repeated parent values collapse only when they are identical after
 *   trimming. The same key with different values is a problem that names the
 *   lines, never a choice between them.
 */

/** One IMAM field a source column can fill. */
export interface SourceField<F extends string = string> {
  id: F;
  label: string;
  required: boolean;
  /** Further headers that prefill this field. Short and documented, never inferred. */
  aliases: readonly string[];
}

/** A CSV's header and its data records, as read. Line numbers count the header as line 1. */
export interface SourceTable { header: string[]; records: string[][] }

/** Each field's chosen column index. A field without an entry is "Not in this file". */
export type ColumnMap = Record<string, number>;

export function normaliseHeader(text: string): string {
  return text.trim().toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
}

/** The header columns that name a field exactly: its id, its label or an alias. */
function namesOf(field: SourceField): Set<string> {
  return new Set([field.id, field.label, ...field.aliases].map(normaliseHeader));
}

/** The first suggestion: a field gets a column only when exactly one header names it. */
export function prefillColumns(fields: readonly SourceField[], header: readonly string[]): ColumnMap {
  const map: ColumnMap = {};
  const normalised = header.map(normaliseHeader);
  for (const field of fields) {
    const names = namesOf(field);
    const matches = normalised.flatMap((name, index) => names.has(name) ? [index] : []);
    if (matches.length === 1) map[field.id] = matches[0];
  }
  return map;
}

/** The required fields the map leaves without a column, in field order. */
export function missingFields<F extends string>(fields: readonly SourceField<F>[], map: ColumnMap): SourceField<F>[] {
  return fields.filter((field) => field.required && map[field.id] === undefined);
}

/** The header columns no field reads. They are shown, then ignored. */
export function unreadColumns(header: readonly string[], map: ColumnMap): string[] {
  const read = new Set(Object.values(map));
  return header.flatMap((name, index) => read.has(index) ? [] : [name.trim() || `column ${index + 1}`]);
}

/** One data record's mapped values, trimmed; a field without a column reads as blank. */
export function valuesOf<F extends string>(fields: readonly SourceField<F>[], map: ColumnMap, record: readonly string[]): Record<F, string> {
  return Object.fromEntries(fields.map((f) => [f.id, map[f.id] === undefined ? "" : (record[map[f.id]] ?? "").trim()])) as Record<F, string>;
}

/** A source status as the server spells it: lower case, separators as underscores ("Leave of absence" is leave_of_absence). */
/** A field's label inside a sentence: "Learner source ID" reads as "learner source ID". */
export function inSentence(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/** Each field's label as a sentence names it, for problems. */
export function sentenceLabels(fields: readonly SourceField[]): Record<string, string> {
  return Object.fromEntries(fields.map((f) => [f.id, inSentence(f.label)]));
}

/** A CSV's records as a table: the first record is the header. */
export function tableOf(records: string[][]): SourceTable {
  return { header: records[0] ?? [], records: records.slice(1) };
}

/** The data records' line numbers: the header is line 1. */
export function lineOf(offset: number): number {
  return offset + 2;
}

export function foldStatus(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function lineList(lines: readonly number[]): string {
  return lines.length === 1 ? `line ${lines[0]}` : `lines ${lines.join(", ")}`;
}

interface Gathered { values: Record<string, string>; lines: number[]; seen: Map<string, Map<string, number[]>> }

export interface Collapse {
  add(key: string, values: Record<string, string>, line: number): void;
  /** Every key whose lines agree, with its first line's values and every line it came from. */
  entries(): Array<{ key: string; values: Record<string, string>; lines: number[] }>;
  /** One sentence per key and field whose lines disagree, naming each value's lines. */
  problems(): string[];
}

/**
 * Parent rows collapsed by key. The first line's values stand for the key;
 * every later line must repeat them exactly, or the key is a named conflict.
 */
export function collapse(noun: string, labels: Record<string, string>): Collapse {
  const keys = new Map<string, Gathered>();
  const disagrees = (g: Gathered) => [...g.seen.values()].some((v) => v.size > 1);
  return {
    add(key, values, line) {
      const gathered: Gathered = keys.get(key) ?? { values, lines: [], seen: new Map() };
      keys.set(key, gathered);
      gathered.lines.push(line);
      for (const [field, value] of Object.entries(values)) {
        const byValue = gathered.seen.get(field) ?? new Map<string, number[]>();
        byValue.set(value, [...(byValue.get(value) ?? []), line]);
        gathered.seen.set(field, byValue);
      }
    },
    entries: () => [...keys].filter(([, g]) => !disagrees(g)).map(([key, g]) => ({ key, values: g.values, lines: g.lines })),
    problems: () => [...keys].flatMap(([key, g]) => [...g.seen].filter(([, v]) => v.size > 1).map(([field, v]) =>
      `${noun} ${key}: ${labels[field] ?? field} differs · ${[...v].map(([value, lines]) => `${value ? JSON.stringify(value) : "blank"} on ${lineList(lines)}`).join(" · ")}`)),
  };
}
